import { useCallback, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import {
  currentMilestone,
  loadDismissed,
  parseDecimal,
  resolveBannerState,
  setInfoDismissed,
  setWarningDismissed,
} from '@/helpers/w9Banner';
import { useTranslation } from '@/i18n';
import { ServerStatus } from '@/models/globalModels';
import { BannerVariant, DismissedState, TaxContext } from '@/models/taxModels';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { doGetTaxContext } from '@/redux/tax/actions';

import Icon, { IconProps } from '../Icon';

const VARIANT_ICON: Record<BannerVariant, IconProps['icon']> = {
  info: 'circle-info',
  warning: 'triangle-exclamation',
  blocked_threshold: 'circle-exclamation',
  blocked_onboarding: 'triangle-exclamation',
};

const VARIANT_MODIFIER: Record<BannerVariant, string> = {
  info: 'W9Banner--info',
  warning: 'W9Banner--warning',
  blocked_threshold: 'W9Banner--blocked-threshold',
  blocked_onboarding: 'W9Banner--blocked-onboarding',
};

const VARIANT_COPY_KEY: Record<BannerVariant, string> = {
  info: 'info',
  warning: 'warning',
  blocked_threshold: 'blockedThreshold',
  blocked_onboarding: 'blockedOnboarding',
};

const DISMISSABLE_VARIANTS: BannerVariant[] = ['info', 'warning'];

const formatThreshold = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return '$0';
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

export default function W9Banner() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const affiliateId = useAppSelector((state) => state.auth.ambassadorProfile?.uid);
  const taxContext = useAppSelector((state) => state.tax.taxContext);
  const taxStatus = useAppSelector((state) => state.tax.taxContextStatus);

  const [dismissed, setDismissed] = useState<DismissedState>(() =>
    affiliateId ? loadDismissed(affiliateId) : { version: 1, infoMilestone: null, warningDismissed: false },
  );

  useEffect(() => {
    if (affiliateId) setDismissed(loadDismissed(affiliateId));
  }, [affiliateId]);

  useEffect(() => {
    if (!affiliateId) return;
    if (taxStatus === ServerStatus.IDLE) {
      dispatch(doGetTaxContext());
    }
  }, [affiliateId, taxStatus, dispatch]);

  useEffect(() => {
    if (!affiliateId) return;
    const handler = (): void => {
      dispatch(doGetTaxContext());
    };
    document.addEventListener('ambassador:w9Submitted', handler);
    return () => document.removeEventListener('ambassador:w9Submitted', handler);
  }, [affiliateId, dispatch]);

  const variant = useMemo(() => resolveBannerState(taxContext as TaxContext | undefined, dismissed), [taxContext, dismissed]);

  const handleDismiss = useCallback(() => {
    if (!affiliateId || !taxContext || !variant) return;
    if (variant === 'info') {
      const milestone = currentMilestone(parseDecimal(taxContext.cash_earnings_ytd));
      setInfoDismissed(affiliateId, milestone);
      setDismissed((prev) => ({ ...prev, infoMilestone: milestone }));
    } else if (variant === 'warning') {
      setWarningDismissed(affiliateId);
      setDismissed((prev) => ({ ...prev, warningDismissed: true }));
    }
  }, [affiliateId, taxContext, variant]);

  const handleOpenW9 = useCallback(() => {
    if (!affiliateId) return;
    document.dispatchEvent(
      new CustomEvent('ambassador:openW9Form', {
        detail: { affiliateId },
      }),
    );
  }, [affiliateId]);

  if (!variant || !taxContext) return null;

  const reportingThreshold = formatThreshold(taxContext.tax_settings.reporting_threshold);
  const copyKey = VARIANT_COPY_KEY[variant];
  const title = t(`w9Banner.${copyKey}.title`, { threshold: reportingThreshold });
  const body = t(`w9Banner.${copyKey}.body`);
  const dismissable = DISMISSABLE_VARIANTS.includes(variant);

  return (
    <div className={classNames('W9Banner', VARIANT_MODIFIER[variant])} role="status">
      <div aria-hidden="true" className="W9Banner__icon">
        <Icon icon={VARIANT_ICON[variant]} />
      </div>
      <div className="W9Banner__content">
        <p className="W9Banner__title">{title}</p>
        <p className="W9Banner__body">{body}</p>
      </div>
      <button className="W9Banner__cta" onClick={handleOpenW9} type="button">
        {t('w9Banner.cta')}
      </button>
      {dismissable ? (
        <button aria-label={t('w9Banner.dismiss')} className="W9Banner__dismiss" onClick={handleDismiss} type="button">
          <Icon icon="xmark" />
        </button>
      ) : null}
    </div>
  );
}
